/**
 * Public surface of `@dentacore/ui`.
 *
 * The design system is CSS-first: the tokens are custom properties, so a
 * component styles itself with `var(--c-accent)` and inherits both themes for
 * free. Import the stylesheets once, at the application's entry point:
 *
 * ```ts
 * import "@dentacore/ui/fonts.css";
 * import "@dentacore/ui/tokens.css";
 * ```
 *
 * The values are exported below as well, for the rare case that needs a token
 * in JavaScript — a canvas, a chart, an inline SVG fill. **Prefer the CSS
 * variable.** These constants cannot follow a theme change; the variables can.
 */

/** Matches `--font-display`. Serif, 24px and up. */
export const FONT_DISPLAY = '"Source Serif 4", Charter, Georgia, serif';

/** Matches `--font-text`. Everything else. */
export const FONT_TEXT = 'Inter, "Avenir Next", "Segoe UI", system-ui, sans-serif';

/** Matches `--font-mono`. Figures that must align in a column. */
export const FONT_MONO = "ui-monospace, Menlo, Consolas, monospace";

/**
 * The two themes the token file defines. `system` is not one of them — it is
 * the absence of a `data-theme` attribute, which lets the OS preference decide.
 */
export const THEMES = ["light", "dark"] as const;
export type ThemeName = (typeof THEMES)[number];

/**
 * The two styles (CF-D6): **Glass**, the default — frosted panes over a soft
 * backdrop — and **Solid**, the opaque surfaces `tokens:check` measures. Set as
 * `data-style` on `<html>`, independent of the theme.
 */
export const STYLES = ["glass", "solid"] as const;
export type StyleName = (typeof STYLES)[number];
